import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { OddsFormat } from "../bullrun/enums.js";
import { usePlatformObservability } from "../observability/PlatformObservabilityContext.js";
const DEFAULT_SETTINGS = {
    oddsFormat: OddsFormat.Percent,
};
const AppSettingsContext = createContext(undefined);
export const AppSettingsProvider = ({ load, save, children }) => {
    const observability = usePlatformObservability();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        let cancelled = false;
        load()
            .then((loaded) => {
            if (cancelled)
                return;
            if (loaded)
                setSettings((prev) => ({ ...prev, ...loaded }));
            setIsLoaded(true);
        })
            .catch((error) => {
            if (cancelled)
                return;
            // Load failure is non-fatal: fall through to DEFAULT_SETTINGS so the
            // app is not left stuck on a loading screen, and surface the error
            // via platform observability (CLAUDE.md: fail loud, not wrong).
            observability.error("AppSettingsProvider: failed to load persisted settings", error);
            setIsLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [load, observability]);
    const setOddsFormat = useCallback((format) => {
        // Compute the new snapshot outside the state updater. React state
        // updaters must be pure; invoking `save()` inside `setSettings((prev) => …)`
        // would let Strict Mode / concurrent rendering fire persistence twice
        // for the same logical change.
        setSettings((prev) => ({ ...prev, oddsFormat: format }));
        // `save` may be sync (localStorage) or async (AsyncStorage). Normalize
        // via Promise.resolve and attach a catch so an async failure doesn't
        // become an unhandled rejection.
        Promise.resolve()
            .then(() => save({ oddsFormat: format }))
            .catch((error) => {
            observability.error("AppSettingsProvider: failed to persist settings", error);
        });
    }, [save, observability]);
    const value = useMemo(() => ({
        oddsFormat: settings.oddsFormat,
        setOddsFormat,
        isLoaded,
    }), [settings.oddsFormat, setOddsFormat, isLoaded]);
    return _jsx(AppSettingsContext.Provider, { value: value, children: children });
};
export function useAppSettings() {
    const ctx = useContext(AppSettingsContext);
    if (!ctx) {
        throw new Error("useAppSettings must be used within an AppSettingsProvider");
    }
    return ctx;
}
